import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useI18n } from '../i18n/context.js'
import { ConfirmDialog } from './ConfirmDialog.js'

/**
 * Asks before a click on a link throws away unsaved work.
 *
 * Every navigation in the interface is a real `<a href>`, so one capturing
 * listener on the document covers the drawer, the site switcher, menus and
 * the search results alike. A link that stays on the same path (a tab or a
 * selection in the query string) keeps the screen and its draft mounted, so
 * it passes through; so do modified clicks and new tabs, which leave this
 * page where it is. `data-cms-dirty` on the body tells other parts of the
 * shell (the site switcher) that a draft is open.
 */
export function DirtyNavigationGuard({ dirty }: { dirty: boolean }) {
  const { t } = useI18n()
  const navigate = useNavigate()
  const [destination, setDestination] = useState<URL | null>(null)

  useEffect(() => {
    document.body.dataset.cmsDirty = dirty ? 'true' : 'false'
    if (!dirty) return () => { delete document.body.dataset.cmsDirty }
    const beforeUnload = (event: BeforeUnloadEvent) => event.preventDefault()
    const capture = (event: MouseEvent) => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
      const anchor = (event.target as Element | null)?.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return
      const url = new URL(anchor.href, window.location.href)
      if (url.origin === window.location.origin && url.pathname === window.location.pathname) return
      event.preventDefault()
      event.stopPropagation()
      setDestination(url)
    }
    window.addEventListener('beforeunload', beforeUnload)
    document.addEventListener('click', capture, true)
    return () => {
      delete document.body.dataset.cmsDirty
      window.removeEventListener('beforeunload', beforeUnload)
      document.removeEventListener('click', capture, true)
    }
  }, [dirty])

  if (!destination) return null
  return <ConfirmDialog
    title={t('guard.title')}
    message={t('guard.message')}
    confirmLabel={t('guard.leave')}
    cancelLabel={t('guard.stay')}
    danger
    onCancel={() => setDestination(null)}
    onConfirm={() => {
      const target = destination
      setDestination(null)
      document.body.dataset.cmsDirty = 'false'
      if (target.origin === window.location.origin) navigate(`${target.pathname}${target.search}${target.hash}`)
      else window.location.assign(target.toString())
    }}
  />
}
